/**
 * Which automation, which of its tabs and which of its runs the address bar
 * names (D9, D11b).
 *
 * QUERY PARAMETERS ON THE EXISTING ROUTE, AND NO NEW ROUTE. A link of the form
 * `/admin/automations?automation=<key>&tab=<tab>` reaches exactly one
 * automation and one tab, survives a reload and follows the browser's back and
 * forward buttons. A route hierarchy (`automations/:key/:tab`) would have meant
 * a router change for a page that has one URL; a query string asks nothing of
 * the router.
 *
 * `run=<id>` OPENS ONE EXECUTION (D11b), and only together with the automation
 * it belongs to and `tab=executions`: `?automation=<key>&tab=executions&run=<id>`.
 * A run is never meaningful on its own — the same id under another automation
 * is a 404 — so EVERY change of automation or tab drops it, and so does closing
 * the workspace. Opening a run and closing it are things the reader did: both
 * are pushes, so back reopens the run that was just closed and forward closes
 * it again. The page removes a `run` it cannot honour with a replace.
 *
 * THE URL IS STATE, NEVER A DATA SOURCE. Nothing here requests anything. Which
 * automation is actually SHOWN is decided by the page from the URL and the
 * list it has loaded (a key that is unknown, or filtered out, is not shown);
 * this hook only reads the address and writes it.
 *
 * TWO KINDS OF WRITE, AND THE DIFFERENCE IS THE HISTORY.
 *   - What the reader DID — chose an automation, chose a tab — is a push: back
 *     and forward step through what they actually looked at.
 *   - What the page CORRECTS — an unknown or filtered-away key, a tab that has
 *     to be bound to the automation it was opened for — is a replace: the
 *     address stops naming something that is not on screen, and the history
 *     gains no entry the reader never made.
 *
 * EXACT AND CASE-SENSITIVE, the `useInvoiceView` rule. An unusable `tab` falls
 * back to Allgemein without being rewritten; the page decides what to do about
 * an unusable `automation`, because only it knows the list.
 *
 * Every write copies the existing parameters first, so anything else in the
 * query string survives.
 */
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import {
  DEFAULT_WORKSPACE_TAB,
  EXECUTIONS_TAB,
  normalizeWorkspaceTab,
} from "./automationWorkspaceTabs";

export const AUTOMATION_PARAM = "automation";
export const TAB_PARAM = "tab";
export const RUN_PARAM = "run";

export default function useAutomationCenterUrl() {
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedKey = searchParams.get(AUTOMATION_PARAM) || null;
  const explicitTab = normalizeWorkspaceTab(searchParams.get(TAB_PARAM));
  const requestedTab = explicitTab ?? DEFAULT_WORKSPACE_TAB;
  const requestedRun = searchParams.get(RUN_PARAM) || null;

  /** The reader chose an automation: it opens on Allgemein. A push. */
  const openAutomation = useCallback(
    (key) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set(AUTOMATION_PARAM, key);
        params.set(TAB_PARAM, DEFAULT_WORKSPACE_TAB);
        params.delete(RUN_PARAM);
        return params;
      });
    },
    [setSearchParams]
  );

  /**
   * The reader chose a tab of the automation on screen. A push.
   *
   * The key is written too: on a bare `/admin/automations` the first row is
   * shown without being named, and a tab click is the moment the address has
   * to start saying which automation that tab belongs to.
   */
  const openTab = useCallback(
    (key, tab) => {
      const value = normalizeWorkspaceTab(tab);
      if (value === null) return;
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (key) params.set(AUTOMATION_PARAM, key);
        params.set(TAB_PARAM, value);
        params.delete(RUN_PARAM);
        return params;
      });
    },
    [setSearchParams]
  );

  /**
   * Make the address name what is on screen. A replace.
   *
   * `null` removes both parameters: nothing is selected, so the address names
   * nothing. Otherwise the key is written with the given tab — Allgemein unless
   * the caller is binding a tab that was already open.
   */
  const replaceAutomation = useCallback(
    (key, tab = DEFAULT_WORKSPACE_TAB) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (!key) {
            params.delete(AUTOMATION_PARAM);
            params.delete(TAB_PARAM);
          } else {
            params.set(AUTOMATION_PARAM, key);
            params.set(TAB_PARAM, normalizeWorkspaceTab(tab) ?? DEFAULT_WORKSPACE_TAB);
          }
          params.delete(RUN_PARAM);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /**
   * The reader opened one run of the automation on screen. A push.
   *
   * Automation and tab are written with it, so the address always says which
   * automation's history the run belongs to.
   */
  const openRun = useCallback(
    (key, runId) => {
      if (!key || !runId) return;
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set(AUTOMATION_PARAM, key);
        params.set(TAB_PARAM, EXECUTIONS_TAB);
        params.set(RUN_PARAM, runId);
        return params;
      });
    },
    [setSearchParams]
  );

  /** The reader closed the run. A push; automation and tab stay. */
  const closeRun = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete(RUN_PARAM);
      return params;
    });
  }, [setSearchParams]);

  /** The page drops a `run` it cannot honour. A replace. */
  const clearRun = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete(RUN_PARAM);
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return {
    requestedKey,
    requestedTab,
    requestedRun,
    hasExplicitTab: explicitTab !== null,
    openAutomation,
    openTab,
    replaceAutomation,
    openRun,
    closeRun,
    clearRun,
  };
}
